import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { shortRepo } from "../lib/chantiers";
import { formatMinutes, plural } from "../lib/format";
import { minToLabel } from "../lib/timelineSlots";
import type {
  AttachTarget,
  ReviewBranch,
  ReviewOverlap,
} from "../lib/weekReview";

export const GIT_ONLY_TITLE = "Git seul, sans activité poste";
export const GIT_ONLY_HELP =
  "Observation, ni comptée ni écartée : à vérifier avant de soumettre.";
export const OVERLAP_BLOCKING =
  "La soumission sera refusée tant que ces chevauchements existent : corrigez les journées concernées.";

/** Lecture des observations du poste : en cours, en échec, période trop longue
 * pour être lue, ou prête. */
export type ObservedState = "loading" | "error" | "too-long" | "ready";

interface Props {
  overlaps: ReviewOverlap[];
  /** Minutes sans projet de la période, null quand elles ne sont pas lues. */
  unassignedMin: number | null;
  /** La liste du temps sans projet, à imputer sur place. */
  children?: ReactNode;
  /** null : TAWA ne calcule pas encore le git seul. */
  gitOnly: ReviewBranch[] | null;
  gitOnlyState: ObservedState;
  attach: AttachTarget[];
  attachState: ObservedState;
  labels: Map<string, string>;
  dayLabel: (key: string) => string;
}

function secondsLabel(seconds: number): string {
  return formatMinutes(Math.round(seconds / 60));
}

function StateNote({ state }: { state: ObservedState }) {
  return (
    <p className="unassigned-empty">
      {state === "loading"
        ? "Chargement…"
        : state === "too-long"
          ? "Observations du poste lisibles sur 31 jours au plus : période trop longue."
          : "Observations du poste indisponibles pour le moment."}
    </p>
  );
}

/** Ce qui reste à régler avant de soumettre, pour soi seul. Les deux points
 * bloquants (temps sans projet, chevauchements) d'abord ; les observations du
 * poste, qui ne bloquent rien, repliées dessous. Ne crée aucun temps depuis une
 * observation. */
export function WeekReviewSection({
  overlaps,
  unassignedMin,
  children,
  gitOnly,
  gitOnlyState,
  attach,
  attachState,
  labels,
  dayLabel,
}: Props) {
  const titleId = useId();
  const clear = unassignedMin === 0 && overlaps.length === 0;
  const observed =
    gitOnlyState === "ready" && attachState === "ready"
      ? (gitOnly?.length ?? 0) + attach.length
      : null;
  return (
    <section className="unassigned-time week-review" aria-labelledby={titleId}>
      <h2 id={titleId} className="section-label">
        Avant de soumettre
      </h2>

      {clear && (
        <p className="week-review-ok">
          Rien ne bloque : tout le temps est imputé et aucune activité ne se
          chevauche.
        </p>
      )}

      {overlaps.length > 0 && (
        <>
          <h3 className="week-review-ttl">Chevauchements</h3>
          <p className="unassigned-empty week-review-blocking" role="alert">
            {overlaps.length} chevauchement{plural(overlaps.length)}.{" "}
            {OVERLAP_BLOCKING}
          </p>
          <ul className="unassigned-list" aria-label="Chevauchements">
            {overlaps.map((o) => (
              <li key={`${o.date}-${o.startMinute}`}>
                <div className="unassigned-row">
                  <span className="text">
                    <span className="ttl">
                      {dayLabel(o.date)}, {minToLabel(o.startMinute)} à{" "}
                      {minToLabel(o.endMinute)}
                    </span>
                    <span className="sub">
                      {o.codes.map((c) => labels.get(c) ?? c).join(" + ")}
                    </span>
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {children}

      <details className="week-review-more">
        <summary>
          Signaux du poste, pour info
          {!!observed && <span className="total">{observed}</span>}
        </summary>
        <h3 className="week-review-ttl">{GIT_ONLY_TITLE}</h3>
        <p className="unassigned-empty">{GIT_ONLY_HELP}</p>
        {gitOnlyState !== "ready" ? (
          <StateNote state={gitOnlyState} />
        ) : gitOnly === null ? (
          <p className="unassigned-empty">
            Pas encore calculé par le serveur de temps.
          </p>
        ) : gitOnly.length === 0 ? (
          <p className="unassigned-empty">
            Aucun temps git sans activité poste sur la période.
          </p>
        ) : (
          <ul className="unassigned-list" aria-label={GIT_ONLY_TITLE}>
            {gitOnly.map((b) => (
              <li key={`${b.repo}\n${b.branch}`}>
                <div className="unassigned-row">
                  <span className="text">
                    <span className="ttl">{shortRepo(b.repo)}</span>
                    <span className="sub">{b.branch || "sans branche"}</span>
                  </span>
                  <span className="v">{secondsLabel(b.seconds)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        <h3 className="week-review-ttl">Dépôts sans projet rattaché</h3>
        {attachState !== "ready" ? (
          <StateNote state={attachState} />
        ) : attach.length === 0 ? (
          <p className="unassigned-empty">
            Tous les dépôts observés sur la période sont rattachés à un projet.
          </p>
        ) : (
          <ul
            className="unassigned-list"
            aria-label="Dépôts sans projet rattaché"
          >
            {attach.map((t) => {
              const name = t.branch
                ? `${shortRepo(t.repo)}, branche ${t.branch}`
                : `le dépôt ${shortRepo(t.repo)}`;
              return (
                <li key={`${t.repo}\n${t.branch}`}>
                  <div className="unassigned-row">
                    <span className="text">
                      <span className="ttl">{shortRepo(t.repo)}</span>
                      <span className="sub">{t.branch || "Dépôt entier"}</span>
                    </span>
                    <span className="v">{secondsLabel(t.seconds)}</span>
                    <Link
                      className="billing-impute"
                      to={t.path}
                      aria-label={`Rattacher ${name} à un projet`}
                    >
                      Rattacher
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </details>
    </section>
  );
}

export default WeekReviewSection;
